import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Edit,
  FileText,
  Link2,
  Lock,
  LockOpen,
  Plus,
  ShieldCheck,
  Trash2,
  Users as UsersIcon,
} from 'lucide-react';
import type { ListUsersQuery, UserListItem, UserStatus } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { UserEditDialog } from '@/components/users/user-edit-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { roleLabelTh } from '@/lib/role-label';
import { useDeleteUser, useUnlockUser, useUpdateUser, usersListQueryOptions } from '@/lib/users';

interface UsersSearch {
  page?: number;
  status?: UserStatus;
  q?: string;
  twoFactor?: 'on' | 'off' | 'required-missing';
}

const STATUS_PILLS: { value: UserStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

const VALID_STATUSES = new Set<UserStatus>(['active', 'invited', 'pending', 'suspended']);

export const Route = createFileRoute('/_authenticated/users/')({
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    page: typeof search.page === 'number' ? search.page : undefined,
    status:
      typeof search.status === 'string' && VALID_STATUSES.has(search.status as UserStatus)
        ? (search.status as UserStatus)
        : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    twoFactor:
      search.twoFactor === 'on' ||
      search.twoFactor === 'off' ||
      search.twoFactor === 'required-missing'
        ? search.twoFactor
        : undefined,
  }),
  component: UsersPage,
});

/**
 * Tight relative format for the author-activity hint. Compresses
 * "X นาที / X ชม. / X วัน" and falls back to absolute MM/YY beyond
 * 60 days where "92 days ago" loses meaningful precision.
 */
function formatRelativeShort(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชม. ที่แล้ว`;
  const day = Math.round(hr / 24);
  if (day < 60) return `${day} วันที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', { year: '2-digit', month: 'short' });
}

function statusBadgeClass(status: UserStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'invited':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'pending':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'suspended':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
}

function UsersPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user: me } = useSession();
  const filter: Partial<ListUsersQuery> = {
    page: search.page ?? 1,
    perPage: 20,
    status: search.status,
    q: search.q,
    twoFactor: search.twoFactor,
  };
  const query = useQuery(usersListQueryOptions(filter));
  const deleteMutation = useDeleteUser();
  const updateMutation = useUpdateUser();
  const unlockMutation = useUnlockUser();

  const canCreate = hasPermission(me, 'user.create');
  const canUpdate = hasPermission(me, 'user.update');
  const canDelete = hasPermission(me, 'user.delete');
  const canReadAudit = hasPermission(me, 'audit.read');
  // Only super_admin should see (or be able to edit) other super_admin rows.
  // Backend enforces the actual block; this just keeps the list clean
  // for regular admins so they don't see/try-and-403 on super_admin entries.
  const isSuperAdmin = me?.role.slug === 'super_admin';

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<UserListItem | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<UserListItem | null>(null);
  const [searchInput, setSearchInput] = useState(filter.q ?? '');

  const allItems = query.data?.items ?? [];
  const items = isSuperAdmin ? allItems : allItems.filter((u) => u.role.slug !== 'super_admin');
  const total = query.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const currentPage = filter.page ?? 1;

  const setStatus = (value: UserStatus | 'all') => {
    void navigate({
      search: { ...search, status: value === 'all' ? undefined : value, page: 1 },
    });
  };
  const goPage = (page: number) => void navigate({ search: { ...search, page } });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { ...search, q: searchInput.trim() || undefined, page: 1 } });
  };

  const openCreate = () => {
    setEditing(undefined);
    setEditOpen(true);
  };
  const openEdit = (u: UserListItem) => {
    setEditing(u);
    setEditOpen(true);
  };
  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: () => setPendingDelete(null),
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              ผู้ใช้งาน
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total} คน` : 'ยังไม่มีผู้ใช้'}
              {/* Active filter count — parity with /posts + /audit + /comments. */}
              {(() => {
                const n = [search.status, search.q, search.twoFactor].filter(Boolean).length;
                return n > 0 ? (
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    กรอง {n}
                  </span>
                ) : null;
              })()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Share-link — parity with /posts + /audit + /comments. */}
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => toast.success('คัดลอกลิงก์รวม filter แล้ว'))
                  .catch(() => {
                    // Clipboard denied — silent.
                  });
              }}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              title="คัดลอกลิงก์หน้านี้ (พร้อม filter)"
            >
              <Link2 className="h-3.5 w-3.5" />
              ลิงก์
            </button>
            {canCreate && (
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                เพิ่มผู้ใช้
              </Button>
            )}
          </div>
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_PILLS.map((p) => {
            const active =
              (search.status ?? 'all') === p.value || (p.value === 'all' && !search.status);
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setStatus(p.value)}
                className={
                  active
                    ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                    : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                {p.label}
              </button>
            );
          })}
          {/* 2FA filter — asymmetric on purpose. The audit use case is
              "show me everyone who should have 2FA but doesn't" so we
              expose just the "no 2FA" toggle. Click flips between
              undefined ↔ 'off'. */}
          <button
            type="button"
            onClick={() =>
              void navigate({
                search: { ...search, twoFactor: search.twoFactor === 'off' ? undefined : 'off' },
              })
            }
            aria-pressed={search.twoFactor === 'off'}
            className={
              search.twoFactor === 'off'
                ? 'rounded-full bg-red-600 px-3 py-1 text-xs font-medium text-white'
                : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }
            title="ผู้ใช้ที่ยังไม่ตั้ง 2FA"
          >
            ไม่มี 2FA
          </button>
          {/* Tighter signal than "ไม่มี 2FA" — filters to users whose
              role appears in the auth.required_2fa_roles setting AND
              has no 2FA. Empty setting → empty list (no false alarms
              when no role requires it). Pairs with the Required2faBanner
              that nags those users on the FE. */}
          <button
            type="button"
            onClick={() =>
              void navigate({
                search: {
                  ...search,
                  twoFactor:
                    search.twoFactor === 'required-missing' ? undefined : 'required-missing',
                },
              })
            }
            aria-pressed={search.twoFactor === 'required-missing'}
            className={
              search.twoFactor === 'required-missing'
                ? 'rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white'
                : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }
            title="role บังคับ 2FA แต่ผู้ใช้ยังไม่เปิด"
          >
            ต้องเปิด 2FA
          </button>
          {/* Master clear — same gesture as /posts + /audit + /comments.
              Shown only when at least one filter is active. */}
          {(search.status || search.q || search.twoFactor) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: {} });
              }}
              className="ml-1 text-xs text-slate-500 hover:underline"
              title="ล้างทุกตัวกรอง"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาอีเมล / username / ชื่อ..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: { ...search, q: undefined, page: 1 } });
              }}
            >
              ล้าง
            </Button>
          )}
        </form>

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <UsersIcon className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ยังไม่มีผู้ใช้</p>
            {canCreate && (
              <Button size="sm" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                เพิ่มผู้ใช้แรก
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 font-medium">ชื่อ / อีเมล</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">สถานะ</th>
                    <th className="px-4 py-3 font-medium">เข้าใช้ล่าสุด</th>
                    {(canUpdate || canDelete || canReadAudit) && (
                      <th className="w-32 px-4 py-3 font-medium" />
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {items.map((u) => {
                    const isMe = u.id === me?.id;
                    return (
                      <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-slate-50">
                            {u.name}
                            {isMe && (
                              <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                คุณ
                              </span>
                            )}
                            {/* Fresh-account pill — security visibility for
                                "is there a new account I didn't create?".
                                Pairs with the RecentUsersCard widget on the
                                dashboard but works on the row level for
                                admins scanning the full list. */}
                            {Date.now() - new Date(u.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                ใหม่
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                            <span>
                              {u.email} · <span className="font-mono">@{u.username}</span>
                            </span>
                            {/* 2FA enrollment indicator — green pill when
                                enrolled, quiet (no pill) when not. The
                                absence is the signal: scan the column for
                                rows missing the badge to spot privileged
                                accounts that haven't enrolled. */}
                            {u.twoFactorEnabled && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                title="ตั้งค่า 2FA แล้ว"
                              >
                                <ShieldCheck className="h-2.5 w-2.5" />
                                2FA
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700 dark:text-slate-300">
                            {roleLabelTh(u.role.slug)}
                          </div>
                          {/* Clickable post-count badge → drill into this
                              author's posts. Hidden at zero so the cell
                              stays compact for new accounts. */}
                          {u.postCount > 0 && (
                            <Link
                              to="/posts"
                              search={{ authorId: u.id }}
                              className="hover:text-brand-600 dark:hover:text-brand-400 mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-500"
                              title={`ดูบทความของ ${u.name}`}
                            >
                              <FileText className="h-2.5 w-2.5" />
                              {u.postCount} บทความ
                            </Link>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {/* Click toggles active ↔ suspended in-place — the
                              two states are the daily-driver lifecycle for
                              an existing account. pending/invited are
                              transient (waiting on user action) and
                              shouldn't be flipped from here. Also block
                              self-suspend to avoid lockout. */}
                          {canUpdate &&
                          !isMe &&
                          (u.status === 'active' || u.status === 'suspended') ? (
                            <button
                              type="button"
                              disabled={updateMutation.isPending}
                              onClick={() =>
                                updateMutation.mutate({
                                  id: u.id,
                                  input: {
                                    status: u.status === 'active' ? 'suspended' : 'active',
                                  },
                                })
                              }
                              title={
                                u.status === 'active' ? 'คลิกเพื่อระงับ' : 'คลิกเพื่อเปิดใช้งาน'
                              }
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium hover:opacity-80 disabled:opacity-50 ${statusBadgeClass(u.status)}`}
                            >
                              {u.status}
                            </button>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadgeClass(u.status)}`}
                            >
                              {u.status}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {u.lastLoginAt
                            ? new Date(u.lastLoginAt).toLocaleString('th-TH', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'ยังไม่เคย'}
                          {/* Tenure hint — small, muted line below
                              lastLogin so the row picks up "new joiner"
                              vs "long-time member" context without
                              committing a full column. */}
                          <div className="mt-0.5 text-[10px] text-slate-400">
                            เข้าร่วม{' '}
                            {new Date(u.createdAt).toLocaleDateString('th-TH', {
                              year: '2-digit',
                              month: 'short',
                            })}
                          </div>
                          {/* Author activity gauge — answers "is this
                              account still producing?" at a glance.
                              Hidden when never published (the postCount
                              badge in the role cell already signals 0). */}
                          {u.lastPublishedAt && (
                            <div
                              className="mt-0.5 text-[10px] text-slate-400"
                              title={`เผยแพร่ ${new Date(u.lastPublishedAt).toLocaleString('th-TH', { hour: '2-digit', minute: '2-digit' })}`}
                            >
                              เผยแพร่ {formatRelativeShort(u.lastPublishedAt)}
                            </div>
                          )}
                          {/* Lockout indicator — only meaningful while
                              lockedUntil is still in the future. Shown
                              as an amber pill below lastLogin so the
                              security signal sits with login context. */}
                          {u.lockedUntil && new Date(u.lockedUntil).getTime() > Date.now() && (
                            <div
                              className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                              title={`ล็อกถึง ${new Date(u.lockedUntil).toLocaleString('th-TH', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })} (${u.failedLoginCount} ครั้งล้มเหลว)`}
                            >
                              <Lock className="h-2.5 w-2.5" />
                              ล็อก
                            </div>
                          )}
                          {/* Sub-threshold failed-login signal — surfaces
                              brute-force probes before they trip the
                              lockout. Hidden at zero to keep the cell
                              quiet during normal use. */}
                          {(!u.lockedUntil || new Date(u.lockedUntil).getTime() <= Date.now()) &&
                            u.failedLoginCount > 0 && (
                              <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                                login ล้มเหลว {u.failedLoginCount}/5
                              </div>
                            )}
                        </td>
                        {(canUpdate || canDelete || canReadAudit) && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {/* Unlock — only when actively locked AND
                                  caller has user.update. Side-effect-free
                                  (no password change) so no confirm dialog;
                                  the audit row + toast are enough trail. */}
                              {canUpdate &&
                                u.lockedUntil &&
                                new Date(u.lockedUntil).getTime() > Date.now() && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => unlockMutation.mutate(u.id)}
                                    disabled={unlockMutation.isPending}
                                    aria-label="ปลดล็อก"
                                    title="ปลดล็อก (ล้าง failed login + lock)"
                                  >
                                    <LockOpen className="h-4 w-4 text-amber-600" />
                                  </Button>
                                )}
                              {/* Audit drill — same gesture as the
                                  PostAuditPanel's "ดูทั้งหมด" link, but
                                  scoped to actions performed BY this
                                  user. Useful for investigating "what
                                  did this account just do?" without
                                  composing the URL by hand. */}
                              {canReadAudit && (
                                <Link
                                  to="/audit"
                                  search={{ userId: u.id }}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                                  title="ดู audit ของผู้ใช้นี้"
                                  aria-label="ดู audit"
                                >
                                  <Activity className="h-4 w-4" />
                                </Link>
                              )}
                              {canUpdate && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEdit(u)}
                                  aria-label="แก้ไข"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete && !isMe && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setPendingDelete(u)}
                                  disabled={deleteMutation.isPending}
                                  aria-label="ลบ"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => goPage(currentPage - 1)}
                >
                  ก่อนหน้า
                </Button>
                <span className="text-slate-500">
                  หน้า {currentPage} จาก {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => goPage(currentPage + 1)}
                >
                  ถัดไป
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <UserEditDialog open={editOpen} onOpenChange={setEditOpen} editing={editing} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ยืนยันการลบผู้ใช้"
        description={
          pendingDelete
            ? `จะลบ "${pendingDelete.name}" (${pendingDelete.email}) — บทความที่เขาเขียนจะยังคงอยู่ แต่บัญชีจะถูก suspend และไม่สามารถ login ได้`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
